// Transport layer for the backend API: auth header, timeout, retry/backoff, and
// error shaping. The resource modules (transactions, debts, …) call apiFetch;
// only formatError is part of the SPA's public surface.

import { config } from '../config';
import { getIdToken } from '../auth';

function sleep(ms: number): Promise<void> {
	return new Promise((resolve) => setTimeout(resolve, ms));
}

function isRetryable(error: Error): boolean {
	const msg = error.message;
	// Retry on network errors, timeouts, and 5xx server errors
	if (msg.includes('NetworkError') || msg.includes('fetch failed') || msg.includes('aborted')) {
		return true;
	}
	const statusMatch = msg.match(/API error (\d+)/);
	if (statusMatch) {
		const status = parseInt(statusMatch[1]);
		return status >= 500 && status < 600;
	}
	return false;
}

export function formatError(error: Error): string {
	const msg = error.message;
	if (msg.includes('Not authenticated')) return 'Please sign in again';
	if (msg.includes('NetworkError') || msg.includes('fetch failed') || msg.includes('aborted')) {
		return 'Network error - please check your connection';
	}
	if (msg.includes('API error 401')) return 'Session expired - please refresh';
	if (msg.includes('API error 403')) return 'Access denied';
	if (msg.includes('API error 404')) return 'Not found';
	if (msg.match(/API error 5\d\d/)) return 'Server error - please try again';
	return msg;
}

async function fetchWithRetry<T>(
	fn: () => Promise<T>,
	maxRetries = 3,
	baseDelay = 1000
): Promise<T> {
	let lastError: Error = new Error('Unknown error');
	for (let attempt = 0; attempt < maxRetries; attempt++) {
		try {
			return await fn();
		} catch (err) {
			lastError = err instanceof Error ? err : new Error(String(err));
			// Only retry on network/5xx errors, not 4xx
			if (!isRetryable(lastError)) throw lastError;
			if (attempt < maxRetries - 1) {
				await sleep(baseDelay * Math.pow(2, attempt));
			}
		}
	}
	throw lastError;
}

// API fetch helper with timeout and retry support
const API_TIMEOUT_MS = 30000;

export async function apiFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
	const token = getIdToken();
	if (!token) {
		throw new Error('Not authenticated');
	}

	const url = `${config.api.url}${path}`;

	return fetchWithRetry(async () => {
		const controller = new AbortController();
		const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT_MS);

		try {
			const response = await fetch(url, {
				...options,
				headers: {
					'Content-Type': 'application/json',
					Authorization: `Bearer ${token}`,
					...options.headers,
				},
				signal: controller.signal,
			});

			if (!response.ok) {
				const errorBody = await response.text();
				throw new Error(`API error ${response.status}: ${errorBody}`);
			}

			return response.json();
		} finally {
			clearTimeout(timeoutId);
		}
	});
}
